/**
 * "Комплаенс" card
 *  - Recording disclaimer (quote, reason)
 *  - Data privacy (violations)
 *  - CRM completeness (missing fields, score)
 *  - Mandatory phrases tag cloud
 *
 * У организации с Э2 встроенных (#2518) строки уведомления, TCPA,
 * конфиденциальности и коды фраз видны только при результате ключа по
 * старому полю — иначе встроенный стоит в группе «Критерии»
 * (`lib/records/builtin-legacy-rows`).
 */

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { mandatoryPhraseCodeLabel } from '@/lib/mandatoryPhraseCodeLabel';
import {
  pausedByAeloPhraseCodes,
  pausedByAeloRow,
  visibleCompliance,
  type BuiltinPausedByAelo,
} from '@/lib/records/builtin-legacy-rows';
import type { BuiltinResultSources } from '@aica/api-types/schemas';
import type { BuiltinCriterionKey } from '@aica/core/call-criteria';
import {
  AnalysisSection,
  Tag,
  scoreTextColor,
  type XMLAnalysisResult,
} from './analysis-utils';

interface ComplianceSectionProps {
  result: XMLAnalysisResult;
  /** `builtinSources` ответа критериев записи; нет — Э2 выключен. */
  builtinSources?: BuiltinResultSources;
  /** `builtinPausedByAelo` ответа: строки этих ключей — с подписью паузы Aelo. */
  builtinPausedByAelo?: BuiltinPausedByAelo;
}

/**
 * Verdict rendering shared by recordingDisclaimer / tcpaCompliance / dataPrivacy:
 * a check the toggle never ran (`passed === null`) is not a failure, so it must
 * not read as `✗`/danger — that's a check that was never performed, not one the
 * call failed. Rows with `applicable === false` are hidden entirely by the caller.
 */
function complianceVerdict(passed: boolean | null): { icon: string; className: string } {
  // == null also catches undefined: records stored before this field existed
  // carry no verdict at all, not a typed null — same "unmeasured" case.
  if (passed == null) {
    return { icon: '—', className: 'text-fg-faint' };
  }
  return passed
    ? { icon: '✓', className: 'text-success' }
    : { icon: '✗', className: 'text-danger' };
}

export function ComplianceSection({
  result,
  builtinSources,
  builtinPausedByAelo,
}: ComplianceSectionProps) {
  const { t } = useTranslation('records');

  // Строки после правила Э2 — тем же расчётом, что и вкладка «Инсайты»
  // панели: нет ни одной (все ушли к встроенным или неприменимы) — карточки
  // нет, а не пустой «Комплаенс».
  const visible = visibleCompliance(result, builtinSources, builtinPausedByAelo);
  if (!visible?.any) return null;
  const {
    recordingDisclaimer,
    tcpaCompliance,
    dataPrivacy,
    crmCompleteness,
    mandatoryPhrases,
  } = visible;

  const crmScore = crmCompleteness?.score;
  const mandatoryUsed = mandatoryPhrases?.used ?? [];
  // Prefer the stable codes (analysis-metric-semantics, Task 3), translated
  // through the records i18n namespace — grouped labels instead of the six
  // different wordings the free-text version produced. Historical analyses
  // (stored before missingCodes existed) have no codes at all, not an empty
  // array, so fall back to the free-text `missing` for those, unchanged.
  const missingCodes = mandatoryPhrases?.missingCodes;
  const mandatoryMissing =
    missingCodes !== undefined
      ? missingCodes.map((code) => mandatoryPhraseCodeLabel(code, t))
      : (mandatoryPhrases?.missing ?? []);
  const showsPhrases = mandatoryUsed.length > 0 || mandatoryMissing.length > 0;
  // Подпись паузы Aelo — как в карточке Compliance Results (#2517): старая
  // строка осталась, потому что встроенный сейчас не оценивает новые звонки.
  const pausedHint = (key: BuiltinCriterionKey) =>
    pausedByAeloRow(builtinSources, key, builtinPausedByAelo) ? (
      <p className="mt-0.5 pl-5 text-micro text-fg-faint">
        {t(
          'records.analysisPanel.compliance.pausedByAelo',
          'Paused by Aelo until re-measured'
        )}
      </p>
    ) : null;
  const pausedPhraseLabels = pausedByAeloPhraseCodes(
    missingCodes,
    builtinSources,
    builtinPausedByAelo
  ).map((code) => mandatoryPhraseCodeLabel(code, t));

  return (
    <AnalysisSection
      title={t('records.analysisPanel.compliance.title', 'Compliance')}
      summary={
        crmScore !== undefined ? (
          <span
            className={cn('font-mono text-xs font-semibold', scoreTextColor(crmScore))}
          >
            CRM {crmScore}
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-2 text-xs">
        {/* Recording Disclaimer — hidden entirely when the toggle never ran */}
        {recordingDisclaimer && (
          <div>
            <div className="flex items-center gap-2">
              <span className={complianceVerdict(recordingDisclaimer.passed).className}>
                {complianceVerdict(recordingDisclaimer.passed).icon}
              </span>
              <span className="text-fg-secondary">
                {t('records.analysisPanel.recordingDisclaimer', 'Recording Disclaimer')}
              </span>
              {recordingDisclaimer.timestamp && (
                <span className="ml-auto font-mono text-fg-faint">
                  {recordingDisclaimer.timestamp}
                </span>
              )}
            </div>
            {recordingDisclaimer.quote && (
              <p className="mt-0.5 pl-5 text-micro italic text-fg-faint">
                &ldquo;{recordingDisclaimer.quote}&rdquo;
              </p>
            )}
            {pausedHint('recording_disclosed')}
          </div>
        )}

        {/* TCPA Compliance — hidden entirely when the toggle never ran */}
        {tcpaCompliance && (
          <div>
            <div className="flex items-center gap-2">
              <span className={complianceVerdict(tcpaCompliance.passed).className}>
                {complianceVerdict(tcpaCompliance.passed).icon}
              </span>
              <span className="text-fg-secondary">
                {t('records.analysisPanel.tcpaCompliance', 'TCPA checklist')}
              </span>
            </div>
            {tcpaCompliance.reason && (
              <p className="mt-0.5 pl-5 text-micro text-fg-faint">
                {tcpaCompliance.reason}
              </p>
            )}
            {pausedHint('tcpa_compliant')}
          </div>
        )}

        {/* Data Privacy — hidden entirely when the toggle never ran */}
        {dataPrivacy && (
          <div>
            <div className="flex items-center gap-2">
              <span className={complianceVerdict(dataPrivacy.passed).className}>
                {complianceVerdict(dataPrivacy.passed).icon}
              </span>
              <span className="text-fg-secondary">
                {t('records.analysisPanel.dataPrivacy', 'Data Privacy')}
              </span>
            </div>
            {pausedHint('data_privacy_violation')}
          </div>
        )}

        {/* CRM Completeness */}
        {crmCompleteness && (
          <div>
            <div className="flex items-center gap-2">
              <span className={crmCompleteness.passed ? 'text-success' : 'text-danger'}>
                {crmCompleteness.passed ? '✓' : '✗'}
              </span>
              <span className="text-fg-secondary">
                {t('records.analysisPanel.crmCompleteness', 'CRM Completeness')}
              </span>
              {crmCompleteness.score !== undefined && (
                <span
                  className={cn(
                    'ml-auto font-mono text-micro',
                    scoreTextColor(crmCompleteness.score)
                  )}
                >
                  {crmCompleteness.score}
                </span>
              )}
            </div>
            {crmCompleteness.missingFields &&
              crmCompleteness.missingFields.length > 0 && (
                <div className="mt-0.5 flex flex-col gap-0.5 pl-5">
                  {crmCompleteness.missingFields.map((f, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-1 text-micro text-warning"
                    >
                      <span>•</span>
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
      </div>

      {/* Mandatory phrases tag cloud */}
      {showsPhrases && (
        <div className="mt-3 border-t border-border pt-3">
          {/* Подпись — всегда: строка, отфильтрованная Э2, приходит без балла
              (он посчитан и по ключам, ушедшим в группу «Критерии»), и метки
              без неё читались бы как продолжение CRM-полей выше. */}
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="text-fg-muted">
              {t('records.analysisPanel.mandatoryPhrases', 'Mandatory Phrases')}
            </span>
            {mandatoryPhrases?.score !== undefined && (
              <span
                className={cn(
                  'font-mono font-semibold',
                  scoreTextColor(mandatoryPhrases.score)
                )}
              >
                {mandatoryPhrases.score}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {mandatoryUsed.map((p) => (
              <Tag key={p} variant="success">
                ✓ {p}
              </Tag>
            ))}
            {mandatoryMissing.map((p) => (
              <Tag key={p} variant="danger">
                ✗ {p}
              </Tag>
            ))}
          </div>
          {pausedPhraseLabels.map((label) => (
            <p key={label} className="mt-1 text-micro text-fg-faint">
              {t(
                'records.analysisPanel.compliance.pausedByAeloPhrase',
                '{{label}}: paused by Aelo until re-measured',
                { label }
              )}
            </p>
          ))}
        </div>
      )}
    </AnalysisSection>
  );
}
